import React, { useEffect, useState } from 'react';
import { ReactComponent as LoaderIcon } from 'images/x-sell-loader.svg';
import { ReactComponent as BankLoading } from 'images/bank-loader.svg';
import StateContainer from 'components/StateContainer';
import { useDispatch, useSelector } from 'react-redux';
import { getLoanOffer } from 'selectors/getLoanOfferData';
import { getApplicationData } from 'selectors/getApplicationData';
import { getApplicationApr, getApplicationData as getApplicationDataThunk, getLoanOfferXSell } from 'thunks';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import { LoanOfferStep } from 'api/LoanOfferApi';
import { getCheckupProgress, getProgressText } from 'components/FinancialCheckup/Analyzing/Analyzing';
import { FlowComponentType } from 'routes/types';
import { ApplicationStatusName } from 'enums/ApplicationStatusName';
import { LoaderResult } from 'enums/FlowNextResults';
import { CurrentFlow } from 'enums/CurrentFlow';
import { setOfferProgress } from 'handlers/loanOffer';
import { getCardData } from 'selectors/getCardData';
import { Dispatch } from 'redux';
import { NO_OFFER_STATUSES } from 'constants/offer';
import { setStudentLoanApplicationId } from 'handlers/studentLoanData';

import styles from './Loader.module.scss';

export const crossSell = (dispatch: Dispatch<any>, applicationId: string) => {
  let applicationIdSaved = false;
  dispatch(
    getLoanOfferXSell({
      applicationId,
      flow: CurrentFlow.AICoach,
      updateCallback: (data) => {
        if (!applicationIdSaved && data.applicationId) {
          applicationIdSaved = true;
          dispatch(setStudentLoanApplicationId(data.applicationId));
        }
        dispatch(setOfferProgress(data));
      },
      resumeLink: `${window.location.href}`,
    }),
  );
};

const Loader = ({ handleNext }: FlowComponentType) => {
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const dispatch = useDispatch();

  const [progress, setProgress] = useState(0);
  const [accountSync, setAccountSync] = useState(false);
  const [totalAccounts, setTotalAccounts] = useState(0);
  const [xSellDone, setXSellDone] = useState(false);

  const offer = useSelector(getLoanOffer);
  const { applicationId } = useSelector(getCardData);
  const { application, isLoading: isLoadingApplication } = useSelector(getApplicationData);

  useEffect(() => {
    const run = async () => {
      if (isLoadingApplication) {
        return;
      }
      if (offer.isCompleted) {
        if (
          offer.response.data.application_status &&
          NO_OFFER_STATUSES.includes(offer.response.data.application_status)
        ) {
          // Offer is not available, so we want to show an error, but don't load the application
          handleNext(LoaderResult.NoOffer);
        } else if (offer.response.data.application_id) {
          let app = application;
          if (app === undefined) {
            // Offer available, and application hasn't been loaded yet, lets load it
            const result = await dispatchWithUnwrap(getApplicationDataThunk(offer.response.data.application_id!));
            app = result.application;
          }

          if (offer.response.data.application_status === ApplicationStatusName.OfferAvailable) {
            // getting the apr from the LMS takes up to 3 seconds, so we pre-fetch it after the loan offer is generated
            dispatch(getApplicationApr(offer.response.data.application_id!));
          }

          // Application should have hard offer, but just in case, we check
          if (app.hardOffer) {
            handleNext(LoaderResult.Success);
          } else {
            analytics.track('Loan Offer Failed - No hard offer', offer);
            handleNext(LoaderResult.CantGenerateOffer);
          }
        } else if (!xSellDone) {
          crossSell(dispatch, applicationId!);
          setXSellDone(true);
        }
      } else if (offer.isLoading && offer.progress) {
        // Offer is loading, so we want to show the progress
        setProgress(getCheckupProgress(offer.progress));
        if (
          [LoanOfferStep.GettingTradelines, LoanOfferStep.SyncingTradelines].includes(offer.progress.step) &&
          offer.progress.totalAccounts
        ) {
          setAccountSync(true);
          setTotalAccounts(offer.progress.totalAccounts || 0);
        }
      } else if (!offer.isLoading && !offer.progress) {
        // Loan offer is not loading, and there is no progress, this means it has not started yet
        crossSell(dispatch, applicationId!);
      }
    };
    run();
  }, [offer]);

  return (
    <div className={styles.container}>
      <StateContainer
        icon={accountSync ? <BankLoading /> : <LoaderIcon />}
        progress={progress}
        title={getProgressText(offer.progress!, accountSync, totalAccounts)}
      />
    </div>
  );
};

export default Loader;
